/**
 * Per-id idempotency helpers for the `snap-status` WebSocket report saves.
 *
 * Spec: .kiro/specs/duplicate-report-save-websocket/{bugfix,design,tasks}.md
 *
 * The backend re-sends the terminal `completed` message for the same
 * `user_reports_id` on every poll cycle while the report is still `pending` —
 * i.e. while the first save is still in flight. Without a guard, the
 * `ws.onmessage` handler issues one save per received message, producing the
 * duplicate `PUT /reports/insert-report/{user_reports_id}` writes.
 *
 * The guard is keyed strictly per id (a `Set<string>`) so that:
 *   - repeated terminal successes for the SAME id issue exactly one save, and
 *   - concurrent distinct reports are NEVER dropped (the set only ever blocks
 *     the id that is already in flight / done).
 *
 * These functions are intentionally pure (no React, no side effects on the
 * caller's state beyond the explicitly mutating helpers) so the dedup decision
 * can be property-tested directly.
 */

/**
 * Decide whether a save should be issued for `id` given the registry of ids
 * that already have a save in flight or completed.
 *
 * @param id - The dedup key (`user_reports_id` for the report branch).
 * @param registry - Set of ids for which a save was already issued/succeeded.
 * @returns `true` iff the id is NOT already present (first terminal success for
 *          this id), `false` otherwise.
 */
export function shouldIssueSave(
    id: string | undefined | null,
    registry: Set<string>
): boolean {
    if (!id) return false;
    return !registry.has(id);
}

/**
 * Mark an id as having a save in flight. Called BEFORE issuing the mutation so
 * that re-sends arriving during the in-flight window are blocked.
 */
export function markSaveIssued(id: string, registry: Set<string>): void {
    registry.add(id);
}

/**
 * Release an id from the registry so a legitimate retry can re-save. Called on
 * save error only — terminal success keeps the id in the registry to block
 * poll-cycle re-sends.
 */
export function releaseSave(id: string, registry: Set<string>): void {
    registry.delete(id);
}
